import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { ReactComponent as Calender } from '../../../../assets/icons/calender.svg';
// import { ReactComponent as Refresh } from '../../../../assets/icons/refresh-fill-icon.svg';
import DefaultApiLimit from '../../../../config/defaultLimit';
import {
  RequestStationActivityInterface,
  StationActivityInterface,
} from '../../../../interface/station-activity-interface';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setNotificationDetails } from '../../../../redux/slice/NotificationSlice';
import {
  disableStationActivityFiltersApplied,
  fetchStationActivity,
  fetchStationActivityFilters,
  updateStationActivityFilters,
  setSortData,
  setLastUpdatedField,
} from '../../../../redux/slice/StationActivitySlice';
import MyButton from '../../../../shared-components/button/Button';
import CustomTable from '../../../../shared-components/custom-table/CustomTable';
import OcppLogSidePanel from '../../../../shared-components/table-side-panel/OcppLogsSIdePanel';
import { GetParametersFromCommonFilters } from '../../../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoHHMM from '../../../../shared-functions/ConvertUTCtoHHMM';
import { exportCSV, exportPDF } from '../../../../shared-functions/ExportFile';
import { FormatUtcString } from '../../../../shared-functions/FormatUtcString';
import stationManagementApi from '../../../../utils/api/station-management';

/**
 * Station activity component
 * @returns
 */
function StationActivity() {
  const { t } = useTranslation('stationManagement');
  const dispatch = useAppDispatch();
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [searchParameters, setSearchParameters] = useSearchParams();
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const {
    stationsActivity,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    filterListLoading,
    filters,
    isFilterUpdated,
    sortBy,
    sortField,
    LastUpdatedFiled,
  } = useAppSelector((state) => state.stationActivity);
  const { selectedChargingPoint } = useAppSelector((state) => state.stationManagement);

  const column = [
    {
      label: t('date'),
      accessorKey: 'created_at' as keyof StationActivityInterface,
      sortAllow: true,

      /**
       * Converting timestamp to UTC date
       * @param stationActivity
       * @returns
       */
      customData: (stationActivity: StationActivityInterface) => (
        <p>{ConvertUTCtoDate(stationActivity.created_at)}</p>
      ),
    },
    {
      label: t('time'),
      accessorKey: 'time' as keyof StationActivityInterface,
      sortAllow: true,
      /**
       * Converting timestamp to UTC time
       * @param stationActivity
       * @returns
       */
      customData: (stationActivityTime: StationActivityInterface) => (
        <p>{FormatUtcString(ConvertUTCtoHHMM(stationActivityTime.created_at))}</p>
      ),
    },
    {
      label: t('type'),
      accessorKey: 'type' as keyof StationActivityInterface,
      sortAllow: false,

      /**
       * Converting timestamp to UTC time
       * @param stationActivity
       * @returns
       */
      customData: (stationActivity: StationActivityInterface) => {
        const type = stationActivity.type?.toLowerCase();

        return (
          <p className={`${type === 'in' ? 'type-in' : 'type-out'}`}>{type ? t(type) : '-'}</p>
        );
      },
    },
    {
      label: t('messageType'),
      accessorKey: 'action' as keyof StationActivityInterface,
    },
    {
      label: t('message'),
      accessorKey: 'message' as keyof StationActivityInterface,
      columnWidth: 500,
    },
  ];

  /**
   * Handle the fetch the data in api function
   * @param data
   */
  const fetchData = (data: RequestStationActivityInterface) => {
    if (!loading && selectedChargingPoint) {
      dispatch(
        fetchStationActivity({
          ...data,
          chargingStation: selectedChargingPoint?.id,
        }),
      );
    }
  };

  /**
   * Current columns for export
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('date'), accessorKey: 'created_at' },
    { label: t('time'), accessorKey: 'time' },
    { label: t('type'), accessorKey: 'type' },
    { label: t('messageType'), accessorKey: 'action' },
    { label: t('message'), accessorKey: 'message' },
  ];

  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Export data function for pdf and csv
   * @param {string} type pdf or csv
   */
  const exportDataPdfOrCSV = (type: string) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    setExportLoader(true);
    const payload = {
      ...queryParameters,
      chargingStation: selectedChargingPoint?.id,
    };
    if (sortBy) payload.sortBy = sortBy;
    if (sortField) payload.sortField = sortField;
    stationManagementApi
      .fetchStationActivityExport(payload)
      .then((response) => {
        // Convert data to CSV format
        const headers = exportCurrentColumns.map((col) => col.label);
        const rows = response.data.map((row: StationActivityInterface, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;
              case 'created_at':
                return ConvertUTCtoDate(row?.created_at) || 'N/A';
              case 'time':
                return ConvertUTCtoHHMM(row?.created_at) || 'N/A';
              default:
                return (row as any)[col?.accessorKey] || 'N/A';
            }
          }),
        );
        if (type === 'csv') {
          exportCSV(headers, rows, `${t('stationActivity')}.csv`);
        } else {
          exportPDF(headers, rows, `${t('stationActivity')}.pdf`);
        }
        setExportLoader(false);
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 404,
              message: error?.response?.data?.message || t('somethingWentWrong'),
            },
          }),
        );
      });
  };
  /**
   * Function to handle data limit change
   */
  const handleLimitChange = (changeLimit: number) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      page: DefaultApiLimit.page,
      limit: changeLimit,
      chargingStation: selectedChargingPoint?.id,
      last15min: false,
      sortBy,
      sortField,
    });
  };
  /**
   * Function to handle page change
   * @param pageNumber
   */
  const handlePageChange = (pageNumber: number) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      page: pageNumber,
      limit,
      last15min: false,
      sortBy,
      sortField,
    });
  };
  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      page,
      limit,
      last15min: false,
      sortBy,
      sortField,
    });
  };

  /**
   * Function for sorting table data
   * @param sortData
   */
  const handleSortFunction = (sortData: { sortBy: 'asc' | 'desc'; sortField: string }) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField === 'time' ? 'created_at' : sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
      last15min: false,
    });
  };

  /**
   * Function to add search params, side panel will open automatically
   * @param logId
   */
  const handleOpenSidePanelOnRowClick = (stationId: string) => {
    // Set or update the logId param
    searchParameters.set('stationId', stationId);

    // Apply the updated search params to the URL
    setSearchParameters(searchParameters);
  };

  useEffect(() => {
    if (!selectedChargingPoint?.id) return;

    const currentId = selectedChargingPoint.id;
    const lastId = sessionStorage.getItem('StationActivityFilters');
    const queryParameters = GetParametersFromCommonFilters(filters);

    // If same id as last time -> do nothing
    if (lastId !== currentId || filters.length === 1) {
      dispatch(fetchStationActivityFilters({ chargingStation: currentId }));
    }
    if (stationsActivity.length === 0 || lastId !== currentId || isFilterUpdated) {
      fetchData({
        page: DefaultApiLimit.page,
        limit,
        last15min: false,
        ...queryParameters,
      });

      dispatch(disableStationActivityFiltersApplied());
    }
    // Save this id so next time we can compare
    sessionStorage.setItem('StationActivityFilters', currentId);
  }, [selectedChargingPoint?.id, filters]);

  return (
    <div className="station-activity-wrap">
      <div className="station-activity-table">
        <CustomTable
          version="v2"
          header={column}
          currentPage={page}
          data={stationsActivity}
          serialNo
          sizeChangeButtonRequired
          limit={limit}
          page={page}
          paginate
          totalPages={totalPages}
          totalResults={totalResults}
          loading={loading}
          filterListLoading={filterListLoading}
          filters={filters}
          filtersToDispatch={updateStationActivityFilters}
          filterListToDispatch={fetchStationActivityFilters}
          exportDataPdfOrCSV={exportDataPdfOrCSV}
          exportLoader={exportLoader}
          refreshCurrentPages={handleRefresh}
          rightTopContent={
            <div className="top-right-content">
              <div className="last-15-mins-button">
                <MyButton
                  label={t('last15Mins')}
                  buttonType={LastUpdatedFiled.lastMins ? 'primary-outline-none' : 'none'}
                  svgIcon={<Calender />}
                  onClickFunc={() => {
                    if (selectedChargingPoint) {
                      const newLastMins = !LastUpdatedFiled.lastMins;
                      const now = new Date();
                      const hours = now.getHours();
                      const minutes = now.getMinutes();
                      const ampm = hours >= 12 ? 'PM' : 'AM';
                      const formattedHours = hours % 12 || 12;
                      const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;
                      dispatch(
                        setLastUpdatedField({
                          lastUpdatedTime: `${formattedHours}:${formattedMinutes} ${ampm}`,
                          lastMins: newLastMins,
                        }),
                      );
                      const queryParameters = GetParametersFromCommonFilters(filters);
                      fetchData({
                        page: DefaultApiLimit.page,
                        limit,
                        ...queryParameters,
                        last15min: newLastMins,
                      });
                    }
                  }}
                />
              </div>
            </div>
          }
          leftTopContent={
            <div className="top-right-content">
              <div className="auto-refreshed-time">
                <p>
                  {t('lastUpdated')}: {LastUpdatedFiled.lastUpdatedTime}
                </p>
              </div>
            </div>
          }
          handlePageChange={handlePageChange}
          handleLimitChange={handleLimitChange}
          topHeader={false}
          sortData={{
            sortBy,
            sortField,
          }}
          handleSortChange={handleSortFunction}
          rowClickEvent={handleOpenSidePanelOnRowClick}
          rowClickParamsKey="stationId"
          rowClickSidePanel={<OcppLogSidePanel fetchIdFrom="queryParams" />}
          onHiddenColumnsChange={setHiddenColumns}
          flexDirection
          tableHeight="calc(100vh - 470px)"
        />
      </div>
    </div>
  );
}

export default StationActivity;
